import { HttpErrorResponse, type HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';

import { ToastService } from './toast.service';

/** Pull the BFF's error message out of a failed HTTP response. */
function errorMessage(err: HttpErrorResponse): string {
  // NestJS error body: { statusCode, message, error }. `message` is a string,
  // or — for class-validator / Zod validation errors — an array of strings.
  const body = err.error as { message?: string | string[] } | string | null;
  const raw = typeof body === 'string' ? body : body?.message;
  if (Array.isArray(raw)) return raw.join('; ');
  if (typeof raw === 'string' && raw.trim().length > 0) return raw;
  return err.status > 0
    ? `Request failed (${err.status} ${err.statusText})`
    : 'Could not reach the server.';
}

/**
 * Surfaces API errors to the user: on a failed HTTP response it shows the
 * BFF's error message as a toast, then rethrows so callers can still react.
 *
 * 401 is skipped — the auth flow handles re-login, so a toast would just be
 * noise on top of the redirect.
 */
export const httpErrorToastInterceptor: HttpInterceptorFn = (req, next) => {
  const toast = inject(ToastService);
  return next(req).pipe(
    catchError((err: unknown) => {
      if (err instanceof HttpErrorResponse && err.status !== 401) {
        toast.error(errorMessage(err));
      }
      return throwError(() => err);
    }),
  );
};
